import { useState } from "react";
import { Popover, MenuItem, IconButton, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toggleUser } from "services/users.service";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { useDispatch } from "react-redux";
import { showToast } from "@/redux/actions/toast-actions";

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "danger",
})(({ theme, danger }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  color: danger ? theme.palette.error.main : "inherit",
  "&:hover": {
    backgroundColor: danger
      ? `${theme.palette.error.main}10`
      : theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 200,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export function UserActionsPopover({ user }) {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const isOpen = Boolean(anchorEl);
  const isActive = user?.active;

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggleMutation = useMutation({
    mutationFn: () => toggleUser({ userId: user.userId }),
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Updated successfully", "success"));
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to update", "error"));
    },
  });

  const handleManageProfile = () => {
    handleClose();
    navigate(`/users/customers/profile/${user.userId}`);
  };

  const handleManageTeamMember = () => {
    handleClose();
    navigate(`/users/customers/teams/${user.userId}?invitorId=${user.userId}`);
  };

  const handleViewReferees = () => {
    handleClose();
    navigate(`/users/customers/referees/${user.userId}`);
  };

  const handleManageKyc = () => {
    handleClose();
    navigate(`/users/customers/kyc/${user.userId}`);
  };

  const handleActivityLog = () => {
    handleClose();
    navigate(`/users/customers/activity/${user.userId}`);
  };

  const handleDeactivateClick = () => {
    handleClose();
    setDeactivateOpen(true);
  };

  const handleDialogClose = () => {
    setDeactivateOpen(false);
    toggleMutation.reset();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: "8px", mt: 0.5 },
          },
        }}
      >
        <PopoverContent>
          <StyledMenuItem onClick={handleManageProfile}>
            View Profile
          </StyledMenuItem>

          <StyledMenuItem onClick={handleManageTeamMember}>
            Team Members
          </StyledMenuItem>

          <StyledMenuItem onClick={handleViewReferees}>
            View Referees
          </StyledMenuItem>

          <StyledMenuItem onClick={handleManageKyc}>Manage KYC</StyledMenuItem>

          <StyledMenuItem onClick={handleActivityLog}>
            Activity Log
          </StyledMenuItem>

          <Divider sx={{ my: 0.5 }} />

          {isActive ? (
            <StyledMenuItem onClick={handleDeactivateClick} danger>
              Deactivate User
            </StyledMenuItem>
          ) : (
            <StyledMenuItem onClick={handleDeactivateClick}>
              Activate User
            </StyledMenuItem>
          )}
        </PopoverContent>
      </Popover>

      <CustomConfirmDialog
        open={deactivateOpen}
        onClose={handleDialogClose}
        title={isActive ? "Deactivate User" : "Activate User"}
        description={
          isActive
            ? `Are you sure you want to deactivate ${user?.otherDetails?.organisationName ?? "this user"}? They will lose access immediately.`
            : `Are you sure you want to activate ${user?.otherDetails?.organisationName ?? "this user"}?`
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        variant={isActive ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() => toggleMutation.mutate()}
      />
    </>
  );
}
